import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Layers, Award, Receipt, PlugZap, ShoppingBasket, Sparkles, BadgePercent, Store, Boxes, Send, LogOut, Menu, X, FileBarChart, ShieldCheck, Eye, Network, Newspaper, CreditCard, LifeBuoy } from "lucide-react";
import { NAV_SECTION, useAuth } from "@/context/AuthContext";
import { BrandLogos, useBranding } from "@/lib/branding";
import { WelcomeDialog } from "@/components/WelcomeDialog";
import { ChangePasswordButton } from "@/components/ChangePasswordButton";
import { DataStaleBanner } from "@/components/DataStaleBanner";
import { api } from "@/lib/api";
import { FilterBar } from "@/components/FilterBar";
import { TenantSwitcher } from "@/components/TenantSwitcher";
import { useFilters } from "@/context/FilterContext";
import { HelpButton } from "@/components/manual/HelpButton";
import { NewVersionDialog, SEEN_KEY } from "@/components/manual/NewVersionDialog";
import { BookOpen, Gift } from "lucide-react";
import { isEmbed } from "@/lib/embed";
import { SidebarQuota } from "@/components/MsgQuotaWidget";

const NAV = [
  { to: "/", label: "Statistiche", icon: LayoutDashboard, id: "dashboard" },
  { to: "/clienti", label: "Clienti", icon: Users, id: "clienti" },
  { to: "/kpi-carte", label: "KPI Carte", icon: ShieldCheck, id: "kpi-carte" },
  { to: "/segmenti", label: "Segmenti", icon: Layers, id: "segmenti" },
  { to: "/fedelta", label: "Punti e livelli", icon: Award, id: "fedelta" },
  { to: "/transazioni", label: "Transazioni", icon: Receipt, id: "transazioni" },
  { to: "/punti-vendita", label: "Punti vendita", icon: Store, id: "punti-vendita" },
  { to: "/carta-fedelta", label: "Carta vs senza carta", icon: CreditCard, id: "carta-fedelta" },
  { to: "/cluster", label: "Cluster clienti", icon: Boxes, id: "cluster" },
  { to: "/comunicazioni", label: "Comunicazioni", icon: Send, id: "comunicazioni" },
  { to: "/paniere", label: "Paniere prodotti", icon: ShoppingBasket, id: "paniere" },
  { to: "/promozioni", label: "Impatto promozioni", icon: BadgePercent, id: "promozioni" },
  { to: "/volantini-promo", label: "Analisi Volantini CRM", icon: Newspaper, id: "volantini-promo" },
  { to: "/premi", label: "Premi", icon: Gift, id: "premi" },
  { to: "/analisi-ia", label: "Agenti IA", icon: Sparkles, id: "analisi-ia" },
  { to: "/report", label: "Report", icon: FileBarChart, id: "report" },
  { to: "/impostazioni-api", label: "Connessione dati", icon: PlugZap, id: "impostazioni" },
];

const sectionOf = (path) => NAV_SECTION[(NAV.find((n) => n.to !== "/" && path.startsWith(n.to)) || NAV[0]).id];

const BADGES = {
  demo: ["border-amber-200 bg-amber-50 text-amber-700", "bg-amber-500", "Modalità demo"],
  ok: ["border-emerald-200 bg-emerald-50 text-emerald-700", "bg-emerald-500", "API esterna collegata"],
  imp: ["border-emerald-200 bg-emerald-50 text-emerald-700", "bg-emerald-500", "Dati importati"],
  fail: ["border-rose-200 bg-rose-50 text-rose-700", "bg-rose-500", "API esterna: errore connessione"],
};

const ModeBadge = ({ s }) => {
  const k = s.mode === "imported" ? "imp" : s.mode !== "external" ? "demo" : s.last_test_ok === false ? "fail" : "ok";
  const [cls, dot, label] = BADGES[k];
  return (
    <NavLink to="/impostazioni-api" data-testid="api-status-badge" data-state={k}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot} animate-pulse`} />{label}
    </NavLink>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState({ mode: "demo" });
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const [recharges, setRecharges] = useState(0);
  const [verInfo, setVerInfo] = useState(null);
  const ver = verInfo?.version;
  const loc = useLocation();
  useEffect(() => { api.get("/manual/version").then((r) => setVerInfo(r.data)).catch(() => {}); }, []);
  const unseen = ver && localStorage.getItem(SEEN_KEY) !== ver;
  useEffect(() => { if (ver && loc.pathname.startsWith("/manuale")) localStorage.setItem(SEEN_KEY, ver); }, [ver, loc.pathname]);
  useEffect(() => {
    const tick = () => api.get("/tickets/pending").then((r) => setPending(r.data.pending)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [loc.pathname]);
  useEffect(() => {
    if (user?.role !== "superadmin") return undefined;
    const tick = () => api.get("/admin/recharge-requests", { params: { status: "pending" } }).then((r) => setRecharges(r.data.length)).catch(() => {});
    tick();
    window.addEventListener("crm-recharge-changed", tick);
    const t = setInterval(tick, 60000);
    return () => { clearInterval(t); window.removeEventListener("crm-recharge-changed", tick); };
  }, [loc.pathname, user?.role]);
  const { key } = useFilters();
  const brand = useBranding();
  useEffect(() => { document.title = brand.app_name ? (brand.company_name ? `${brand.app_name} · ${brand.company_name}` : brand.app_name) : "Nouvelle CRM"; }, [brand.app_name, brand.company_name]);
  const readOnly = user?.permissions?.[sectionOf(loc.pathname)] === "read";
  const embed = isEmbed();

  useEffect(() => {
    const load = () => (user?.permissions?.settings || "none") !== "none" && api.get("/settings/api").then((r) => setSettings(r.data)).catch(() => {});
    setOpen(false);
    load();
    window.addEventListener("crm-settings-changed", load);
    return () => window.removeEventListener("crm-settings-changed", load);
  }, [loc.pathname]);

  const navItems = [...NAV.filter((n) => (user?.permissions?.[NAV_SECTION[n.id]] || "none") !== "none"),
    ...(user?.role === "superadmin" && !embed ? [{ to: "/superadmin", label: "SuperAdmin", icon: ShieldCheck, id: "superadmin" }] : []),
    ...(user?.global_admin && !embed ? [{ to: "/circuiti", label: "Circuiti", icon: Network, id: "circuiti" }] : []),
    ...(embed ? [] : [{ to: "/assistenza", label: "Assistenza", icon: LifeBuoy, id: "assistenza" }, { to: "/manuale", label: "Manuale", icon: BookOpen, id: "manuale" }].filter((n) => (user?.permissions?.[NAV_SECTION[n.id]] || "none") !== "none"))];

  if (embed) {
    return (
      <div className="min-h-screen bg-[hsl(210,20%,98%)]" data-testid="embed-layout">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
          <div className="flex h-12 items-center gap-2 overflow-x-auto px-3 sm:px-4">
            <BrandLogos b={brand} size="h-6" narrow />
            <nav className="flex items-center gap-1" data-testid="embed-nav">
              {navItems.map(({ to, label, icon: Icon, id }) => (
                <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${id}-link`} title={label}
                  className={({ isActive }) => `flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition-colors ${isActive ? "bg-blue-50 font-medium text-blue-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.8} /><span className="hidden md:inline">{label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="ml-auto flex shrink-0 items-center gap-2">{readOnly && <span data-testid="readonly-badge" className="hidden items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500 sm:inline-flex"><Eye className="h-3 w-3" />Sola lettura</span>}<FilterBar /><span className="hidden truncate text-[11px] text-slate-600 lg:inline" data-testid="current-user-email">{user?.email}</span></div>
          </div>
        </header>
        <DataStaleBanner />
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6"><Outlet key={key} /></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)]">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className={`flex border-b border-slate-100 px-6 ${Object.keys(brand.logos || {}).length > 1 ? "flex-col justify-center gap-1.5 py-3" : "h-16 items-center gap-2.5"}`}>
          <BrandLogos b={brand} size="h-8" narrow />
          <div className="min-w-0">
            <div className="truncate font-heading text-sm font-semibold text-slate-900" data-testid="sidebar-app-name">{brand.app_name}</div>
            <div className="truncate text-[11px] text-slate-400">{brand.company_name || "Pannello gestionale"}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
          {navItems.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${id}-link`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-blue-50 font-medium text-blue-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
              <Icon className="h-4 w-4" strokeWidth={1.8} />
              {label}
              {id === "assistenza" && pending > 0 && <span data-testid="nav-assistenza-badge" title="Ticket in attesa di risposta" className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-600 px-1.5 text-[11px] font-semibold text-white">{pending > 99 ? "99+" : pending}</span>}
              {id === "superadmin" && recharges > 0 && <span data-testid="nav-superadmin-badge" title="Richieste di ricarica plafond in attesa" className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">{recharges > 99 ? "99+" : recharges}</span>}
              {id === "manuale" && unseen && <span data-testid="nav-manuale-new" title={`Nuova versione ${ver}`} className="ml-auto rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700">v{ver}</span>}
            </NavLink>
          ))}
        </nav>
        <SidebarQuota />
        <div className="border-t border-slate-100 p-4">
          <div className="truncate text-xs text-slate-500" data-testid="current-user-email">{user?.email}</div>
          <div className="truncate text-[11px] text-slate-400" data-testid="current-user-profile">{user?.profile_name}</div>
          <ChangePasswordButton />
          {ver && <NavLink to="/manuale/novita" data-testid="sidebar-version" className="mt-2 inline-block font-mono text-[11px] text-slate-400 hover:text-blue-700">Versione {ver}</NavLink>}
          <button onClick={logout} data-testid="logout-button" className="mt-2 flex items-center gap-2 text-sm text-slate-600 hover:text-rose-600 transition-colors">
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-slate-900/30 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <TenantSwitcher />
          <div className="flex items-center gap-3">{readOnly && <span data-testid="readonly-badge" className="hidden items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-500 sm:inline-flex"><Eye className="h-3.5 w-3.5" />Sola lettura</span>}<FilterBar />{(user?.permissions?.settings || "none") !== "none" && <span className="hidden sm:block"><ModeBadge s={settings} /></span>}{(user?.permissions?.manual || "none") !== "none" && <HelpButton />}</div>
        </header>
        <DataStaleBanner />
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">
          <Outlet key={key} />
        </main>
      </div>
      <WelcomeDialog />
      <NewVersionDialog info={verInfo} />
    </div>
  );
}
